////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ft3d/ft3d-backdrop-kits.ts — procedural backdrop ELEMENT KITS (PROCEDURAL_BACKDROP_PLAN.md §1/§5.1).
// ----------------------------------------------------------------------------------------------------
// The theme "kit of parts": deterministic SVG element builders (trees, ridges, wisps, speckles,
// coral, kelp, rubble) that compose into seamless-X tiling far/near bands. Extracted verbatim
// from ft3d-bg-manifest.ts (Phase 1 — no behaviour change), then threaded with a per-encounter
// SEED (Phase 2) so each encounter of the same theme composes a DISTINCT but on-theme scene.
//
// SEED CONTRACT: every composer is (seed: number) => svg-string, pure + deterministic — the same
// seed always renders the same scene across reloads/players. seed 0 reproduces the pre-seeding
// fixed art. The caller (FT3DParallaxBackground) derives seed from encounter_backdrop_seed (GM
// re-roll field) falling back to the encounter id — see PROCEDURAL_BACKDROP_PLAN.md §2.
//
// TILING CONTRACT (unchanged): every band is seamless on X — shapes near an edge are wrap-
// duplicated (dupX) or edge-pinned (ridgePath enters/exits at the same height). AI-art kits
// (Opus, plan §4) replace these builders per theme later; these stay as the universal fallback.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/** A seeded tile composer — returns the inner SVG document for one seamless-X band. */
export type FT3DSvgComposer = (seed: number) => string;

// Tile canvases (units, mapped to world-feet by the manifest's heightFt).
export const NEAR_W = 1024;
export const NEAR_H = 320;
export const FAR_W = 1024;
export const FAR_H = 256;

/** Deterministic 0..1 hash — stable across builds (seeded sin-hash, no Math.random). */
export const rnd = (i: number): number => {
    const x = Math.sin(i * 127.1 + 311.7) * 43758.5453;
    return x - Math.floor(x);
};

export const r1 = (n: number): number => Math.round(n * 10) / 10;

export const svgUrl = (svg: string): string => `data:image/svg+xml,${encodeURIComponent(svg)}`;

/**
 * Bound a 32-bit hash seed for use as an rnd() offset. Composers ADD this to their per-variant
 * base seeds; keeping it small preserves sin-hash float precision and makes seed 0 a no-op
 * (identical to the pre-seeding fixed art).
 */
export const boundSeed = (seed: number): number => Math.abs(Math.trunc(seed)) % 100003;

// ─── Forest kit (Phase 0 pilot parts) ─────────────────────────────────────────────────────

/** Conifer: stacked drooping fronds + trunk, mirrored around cx. */
export function pinePath(cx: number, baseY: number, h: number, w: number): string {
    const tiers = 5;
    const apexY = baseY - h;
    const right: Array<[number, number]> = [];
    for (let t = 1; t <= tiers; t++) {
        const y = apexY + h * 0.82 * (t / tiers);
        const half = (w / 2) * (0.3 + 0.7 * (t / tiers));
        right.push([cx + half, y]);
        if (t < tiers) right.push([cx + half * 0.52, y]);
    }
    const trunkW = Math.max(2, w * 0.07);
    right.push([cx + trunkW, baseY - h * 0.16]);
    right.push([cx + trunkW, baseY]);
    const left = right.map(([x, y]) => [2 * cx - x, y] as [number, number]).reverse();
    const pts = [...right, ...left].map(([x, y]) => `${r1(x)} ${r1(y)}`).join(' L');
    return `M${r1(cx)} ${r1(apexY)} L${pts} Z`;
}

/** Broadleaf: lobed canopy blob (quadratic bumps around an ellipse) + tapered trunk. */
export function oakPath(cx: number, baseY: number, h: number, w: number, seed: number): string {
    const trunkW = Math.max(2.5, w * 0.09);
    const cy = baseY - h * 0.62;
    const rx = w / 2;
    const ry = h * 0.4;
    const n = 9;
    let d = '';
    for (let i = 0; i <= n; i++) {
        const a = (i / n) * Math.PI * 2;
        const wob = 0.85 + 0.3 * rnd(seed + (i % n));
        const x = cx + Math.cos(a) * rx * wob;
        const y = cy + Math.sin(a) * ry * wob;
        if (i === 0) {
            d += `M${r1(x)} ${r1(y)} `;
        } else {
            const am = ((i - 0.5) / n) * Math.PI * 2;
            const qx = cx + Math.cos(am) * rx * (wob + 0.28);
            const qy = cy + Math.sin(am) * ry * (wob + 0.28);
            d += `Q${r1(qx)} ${r1(qy)} ${r1(x)} ${r1(y)} `;
        }
    }
    d += `Z M${r1(cx - trunkW)} ${baseY} L${r1(cx - trunkW * 0.7)} ${r1(cy + ry * 0.6)} L${r1(cx + trunkW * 0.7)} ${r1(cy + ry * 0.6)} L${r1(cx + trunkW)} ${baseY} Z`;
    return d;
}

/** Rolling canopy ridge across the full tile width; both edges at the same height (seamless). */
export function ridgePath(width: number, height: number, topFrac: number, amp: number, seed: number): string {
    const edgeY = height * topFrac;
    const steps = 12;
    let d = `M0 ${height} L0 ${r1(edgeY)} `;
    let prevX = 0;
    let prevY = edgeY;
    for (let i = 1; i <= steps; i++) {
        const x = (width * i) / steps;
        const y = i === steps ? edgeY : edgeY + (rnd(seed + i) - 0.5) * 2 * amp * height;
        const qx = (prevX + x) / 2;
        const qy = Math.min(prevY, y) - amp * height * (0.4 + 0.6 * rnd(seed + 40 + i));
        d += `Q${r1(qx)} ${r1(qy)} ${r1(x)} ${r1(y)} `;
        prevX = x;
        prevY = y;
    }
    d += `L${width} ${height} Z`;
    return d;
}

export type TreeSpec = [x: number, kind: 'p' | 'o', h: number];

export function treeRow(specs: TreeSpec[], baseY: number, seed: number): string {
    return specs
        .map(([x, kind, h], i) =>
            kind === 'p'
                ? pinePath(x, baseY, h, h * 0.42)
                : oakPath(x, baseY, h, h * 0.78, seed + i * 7))
        .join(' ');
}

/**
 * Seeded tree LAYOUT (plan §1 layout(seed)): `count` trees in even slots with per-seed jitter
 * (position / species / height), staying clear of the tile edges so repeat-x tiles cleanly.
 */
export function forestTreeSpecs(seed: number, count: number, hMin: number, hMax: number): TreeSpec[] {
    const specs: TreeSpec[] = [];
    const slot = NEAR_W / count;
    for (let i = 0; i < count; i++) {
        const x = r1(slot * i + slot * (0.3 + 0.4 * rnd(seed + i)));
        const kind: 'p' | 'o' = rnd(seed + 31 + i) < 0.55 ? 'p' : 'o';
        const h = Math.round(hMin + (hMax - hMin) * rnd(seed + 61 + i));
        specs.push([x, kind, h]);
    }
    return specs;
}

// NEAR tile: 1024×320 units mapped to 50 ft tall → trees are 28–47 ft. Two rows for depth
// (back row lighter, front row darker) + an understory strip that roots everything to the
// groundline and carries the tile seam.
export const forestNearSvg: FT3DSvgComposer = (seed) => {
    const s = boundSeed(seed);
    return `<svg xmlns='http://www.w3.org/2000/svg' width='${NEAR_W}' height='${NEAR_H}'>` +
        `<path d='${treeRow(forestTreeSpecs(s + 200, 6, 180, 290), NEAR_H, s + 3)}' fill='#2c4636'/>` +
        `<path d='${treeRow(forestTreeSpecs(s + 300, 5, 170, 260), NEAR_H, s + 17)}' fill='#14291a'/>` +
        `<path d='${ridgePath(NEAR_W, NEAR_H, 0.955, 0.02, s + 29)}' fill='#101f13'/>` +
        `</svg>`;
};

// FAR tile: 1024×256 units mapped to 90 ft tall → two misty canopy ridges (blurred at render).
export const forestFarSvg: FT3DSvgComposer = (seed) => {
    const s = boundSeed(seed);
    return `<svg xmlns='http://www.w3.org/2000/svg' width='${FAR_W}' height='${FAR_H}'>` +
        `<path d='${ridgePath(FAR_W, FAR_H, 0.3, 0.05, s + 5)}' fill='#58707e'/>` +
        `<path d='${ridgePath(FAR_W, FAR_H, 0.52, 0.06, s + 11)}' fill='#435a67'/>` +
        `</svg>`;
};

// ─── Cosmic kit (planar / outer_space / underwater parts) ────────────────────────────────

/** Duplicate an x-position across the tile seam so edge-crossing shapes wrap (repeat-x safe). */
export function dupX(x: number, margin: number, w: number): number[] {
    const xs = [x];
    if (x < margin)     xs.push(x + w);
    if (x > w - margin) xs.push(x - w);
    return xs;
}

/** Star/ember/bubble speckle band — tiny varied dots, wrap-duplicated at the seam. */
export function speckleBand(seed: number, count: number, color: string, maxR = 1.4): string {
    let out = '';
    for (let i = 0; i < count; i++) {
        const x = rnd(seed + i) * FAR_W;
        const y = r1(rnd(seed + 100 + i) * FAR_H);
        const r = r1(0.4 + rnd(seed + 200 + i) * maxR);
        const o = r1(0.3 + rnd(seed + 300 + i) * 0.7);
        for (const xx of dupX(x, 4, FAR_W)) {
            out += `<circle cx='${r1(xx)}' cy='${y}' r='${r}' fill='${color}' opacity='${o}'/>`;
        }
    }
    return out;
}

/** Soft blurred cloud/murk wisps (nebula gas, planar haze, deep-water murk). */
export function wispBand(seed: number, colors: string[], count: number, maxOpacity: number): string {
    let out = '';
    for (let i = 0; i < count; i++) {
        const x  = rnd(seed + i) * FAR_W;
        const y  = FAR_H * (0.12 + rnd(seed + 50 + i) * 0.64);
        const rx = 90 + rnd(seed + 90 + i) * 150;
        const ry = 22 + rnd(seed + 130 + i) * 42;
        const c  = colors[i % colors.length];
        const o  = r1(maxOpacity * (0.4 + 0.6 * rnd(seed + 170 + i)));
        for (const xx of dupX(x, rx + 60, FAR_W)) {
            out += `<ellipse cx='${r1(xx)}' cy='${r1(y)}' rx='${r1(rx)}' ry='${r1(ry)}' fill='${c}' opacity='${o}' filter='url(#b)'/>`;
        }
    }
    return out;
}

/** FAR tile wrapper — shared gaussian-blur filter def for the wisps. */
export function cosmicFarSvg(parts: string): string {
    return `<svg xmlns='http://www.w3.org/2000/svg' width='${FAR_W}' height='${FAR_H}'>` +
        `<defs>` +
        `<filter id='b' x='-40%' y='-40%' width='180%' height='180%'><feGaussianBlur stdDeviation='16'/></filter>` +
        `<filter id='bg' x='-60%' y='-60%' width='220%' height='220%'><feGaussianBlur stdDeviation='40'/></filter>` +
        `</defs>` +
        parts + `</svg>`;
}

/** Big soft glowing nebula/plane CORES — heavy-blur (filter bg) colour blobs behind the wisps, for
 * depth + richness. Seeded position/size/colour; wrap-duplicated at the seam. */
export function nebulaGlowBand(seed: number, colors: string[], count: number, maxOpacity = 0.5): string {
    let out = '';
    for (let i = 0; i < count; i++) {
        const x  = rnd(seed + i) * FAR_W;
        const y  = FAR_H * (0.2 + rnd(seed + 40 + i) * 0.5);
        const rx = 130 + rnd(seed + 80 + i) * 190;
        const ry = 60 + rnd(seed + 120 + i) * 80;
        const c  = colors[i % colors.length];
        const o  = r1(maxOpacity * (0.5 + 0.5 * rnd(seed + 160 + i)));
        for (const xx of dupX(x, rx + 90, FAR_W)) {
            out += `<ellipse cx='${r1(xx)}' cy='${r1(y)}' rx='${r1(rx)}' ry='${r1(ry)}' fill='${c}' opacity='${o}' filter='url(#bg)'/>`;
        }
    }
    return out;
}

/** Star clusters + occasional bright HALOED star (soft glow behind a crisp core). Adds sparkle/depth
 * over the flat speckle. Seeded; wrap-duplicated at the seam. */
export function starClusterBand(seed: number, clusters: number, color: string): string {
    let out = '';
    for (let c = 0; c < clusters; c++) {
        const cx = rnd(seed + c) * FAR_W;
        const cy = FAR_H * (0.08 + rnd(seed + 200 + c) * 0.8);
        const n  = 4 + Math.floor(rnd(seed + 240 + c) * 6);
        for (let j = 0; j < n; j++) {
            const x = cx + (rnd(seed + 300 + c * 9 + j) - 0.5) * 70;
            const y = r1(cy + (rnd(seed + 360 + c * 9 + j) - 0.5) * 40);
            const r = r1(0.4 + rnd(seed + 420 + c * 9 + j) * 1.1);
            const o = r1(0.4 + rnd(seed + 480 + c * 9 + j) * 0.6);
            for (const xx of dupX(x, 6, FAR_W)) out += `<circle cx='${r1(xx)}' cy='${y}' r='${r}' fill='${color}' opacity='${o}'/>`;
        }
        // one bright haloed star per cluster
        const bx = cx + (rnd(seed + 540 + c) - 0.5) * 40;
        const by = r1(cy + (rnd(seed + 560 + c) - 0.5) * 24);
        for (const xx of dupX(bx, 10, FAR_W)) {
            out += `<circle cx='${r1(xx)}' cy='${by}' r='${r1(4 + rnd(seed + 580 + c) * 3)}' fill='${color}' opacity='0.28' filter='url(#b)'/>`;
            out += `<circle cx='${r1(xx)}' cy='${by}' r='1.4' fill='#ffffff' opacity='0.95'/>`;
        }
    }
    return out;
}

/** NEAR ground silhouette — two rocky ridges (back glow/lighter, front dark), seamless via ridgePath. */
export function rubbleNearSvg(back: string, front: string, amp: number, seed: number): string {
    return `<svg xmlns='http://www.w3.org/2000/svg' width='${NEAR_W}' height='${NEAR_H}'>` +
        `<path d='${ridgePath(NEAR_W, NEAR_H, 0.46, amp, seed)}' fill='${back}'/>` +
        `<path d='${ridgePath(NEAR_W, NEAR_H, 0.66, amp * 1.3, seed + 13)}' fill='${front}'/>` +
        `</svg>`;
}

/** NEAR seabed with coral heads (oakPath blobs read as coral) for underwater/reef. */
export function coralNearSvg(seed: number): string {
    // Seed jitters position/height around six hand-placed anchor heads (keeps the approved
    // density/rhythm while every encounter's reef differs).
    const anchors: Array<[number, number]> = [[90, 150], [260, 210], [420, 130], [600, 240], [780, 165], [940, 190]];
    const corals = anchors
        .map(([ax, ah], i) => {
            const x = r1(ax + (rnd(seed + 700 + i) - 0.5) * 90);
            const h = Math.round(ah * (0.8 + 0.4 * rnd(seed + 730 + i)));
            return `<path d='${oakPath(x, NEAR_H, h, h * 0.85, seed + 400 + i * 11)}' fill='${i % 2 ? '#6e3a52' : '#8a5038'}' opacity='0.9'/>`;
        })
        .join('');
    return `<svg xmlns='http://www.w3.org/2000/svg' width='${NEAR_W}' height='${NEAR_H}'>` + corals +
        `<path d='${ridgePath(NEAR_W, NEAR_H, 0.9, 0.02, seed + 77)}' fill='#0f2e3c'/></svg>`;
}

/** NEAR swaying kelp strands over a low seabed ridge for underwater/kelp. */
export function kelpNearSvg(seed: number): string {
    let strands = '';
    for (let i = 0; i < 20; i++) {
        const x = 70 + rnd(seed + 500 + i) * (NEAR_W - 140);
        const h = NEAR_H * (0.55 + rnd(seed + 520 + i) * 0.4);
        const sway = 16 + rnd(seed + 540 + i) * 24;
        const w = r1(9 + rnd(seed + 560 + i) * 8);
        const c = i % 3 ? '#0e3d2a' : '#155238';
        strands += `<path d='M${r1(x)} ${NEAR_H} C${r1(x + sway)} ${r1(NEAR_H - h * 0.35)} ${r1(x - sway)} ${r1(NEAR_H - h * 0.7)} ${r1(x + sway * 0.6)} ${r1(NEAR_H - h)}' stroke='${c}' stroke-width='${w}' fill='none' stroke-linecap='round'/>`;
    }
    return `<svg xmlns='http://www.w3.org/2000/svg' width='${NEAR_W}' height='${NEAR_H}'>` + strands +
        `<path d='${ridgePath(NEAR_W, NEAR_H, 0.92, 0.02, seed + 88)}' fill='#0a2433'/></svg>`;
}
